/**
 * `ViewerInfoBar` — shared IDE-style status bar rendered at the top
 * of every WorkspaceFilePane viewer (PDF, DOCX, MD, Image, Text).
 *
 * Composition:
 *   ┌──────────────────────────────────────────────────────┐
 *   │ ← 📄 Alias  · 🏷 doctype · ⚙ extra                  ✕ │
 *   └──────────────────────────────────────────────────────┘
 *
 * - Back arrow — ONLY when this file was reached by following a link out of
 *   another document (`FileRef.openedFrom`). Because the bar is shared, this
 *   one edit gives image, audio, PDF, DOCX, text and markdown the same way
 *   home. See `ai-docs/lexide-viewer-return-to-origin-miniplan.md`.
 * - Alias chip (always rendered, clickable) — opens the view/edit dialog.
 * - Optional viewer-specific chips (slotted via `children`).
 * - [×] close button on the right — wired by the shell.
 *
 * Clicking the alias chip (or the click-anywhere spacer) opens the shared
 * <FileDisplayDetailsDialog> — the single VIEW + EDIT surface for a file's
 * display attributes (alias + description). Per plan §0 rule 6 the internal
 * OS filename/path is NEVER shown here. The display DESCRIPTION now lives in
 * that dialog and in the tree's expand-on-select row — deliberately NOT as an
 * info-bar chip (operator brief 2026-07-03: the tree+dialog approach supersedes
 * the earlier chip).
 */

import { useState, type ReactNode } from "react";
import { displayLabelForPath } from "@/lib/workspace/derive-display-name";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Download, FileText, Loader2, X } from "lucide-react";
import { basename, useWorkspaceFileStore, type FileRef } from "@/stores/useWorkspaceFileStore";
import { useWorkspaceAliasMap } from "@/hooks/useWorkspaceAliasMap";
import { FileDisplayDetailsDialog } from "@/components/shell/FileDisplayDetailsDialog";

/** A file the bar can hand to the user as a download. */
export interface ViewerDownload {
  /** Filename shown in the tooltip. Callers MUST pass a name that keeps
   *  the original EXTENSION — a display title ("Controle de Pagto do
   *  Aluguel") saved without `.xlsx` lands as a file the OS cannot open. */
  filename: string;
  /** Fired on click. The caller owns how the bytes are obtained (see
   *  `useFileDownload`, which defers the read until this fires). */
  onDownload: () => void;
  /** Bytes in flight — the control disables and spins rather than
   *  vanishing, so the affordance doesn't flicker as a file loads. */
  pending?: boolean;
}

interface ViewerInfoBarProps {
  file: FileRef;
  onClose?: () => void;
  /** Extra chips (typically `<InfoChip />`s) slotted between the
   *  alias chip and the right-side spacer. */
  children?: ReactNode;
  /** Override the default details dialog. When set, this fires
   *  instead of the built-in <FileDisplayDetailsDialog> — viewers with
   *  richer metadata (MD's frontmatter) wire their own dialog. */
  onOpenDetails?: () => void;
  /** Render a download control for the file's ORIGINAL bytes.
   *
   *  Wired by every viewer that cannot hand the lawyer the real artifact
   *  through its own body — which is all of them except PDF (whose
   *  document toolbar owns page nav + zoom + download as one cluster).
   *  It matters most on the unsupported-binary path, where the message
   *  literally instructs the lawyer to download the file: before this
   *  existed, that instruction pointed at a control the UI did not have
   *  (operator-flagged 2026-07-31). */
  download?: ViewerDownload;
}

/**
 * The name a file is SHOWN by. Alias-first: an explicitly-passed title wins,
 * then the display overlay's title/alias, then the derived label.
 *
 * ⚖ S1 — the tail used to be `basename(path)`, the raw OS name, directly under
 * a docblock citing "plan §0 rule 6" — the rule that says no file is ever named
 * by its OS name. It derives now, same rule as the agent plane.
 *
 * Shared by the title chip and the back control precisely so the two cannot
 * drift: two copies of this chain is how one of them quietly goes back to
 * showing an OS filename.
 */
function labelFor(ref: FileRef, map: ReturnType<typeof useWorkspaceAliasMap>): string {
  const entry = map.byPath.get(ref.path) ?? null;
  return (
    ref.title ?? entry?.title ?? entry?.alias ?? displayLabelForPath(ref.path, basename(ref.path))
  );
}

export function ViewerInfoBar({
  file,
  onClose,
  children,
  onOpenDetails,
  download,
}: ViewerInfoBarProps) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const map = useWorkspaceAliasMap();
  const openFile = useWorkspaceFileStore((s) => s.openFile);
  const entry = map.byPath.get(file.path) ?? null;
  const currentName = labelFor(file, map);
  const detailsLabel = t("workspace.filePane.metadata.detailsTitle");
  const closeLabel = t("workspace.filePane.closeAriaLabel");
  const handleOpen = onOpenDetails ?? (() => setDetailsOpen(true));
  const downloadLabel = t("workspace.filePane.viewer.download", {
    name: download?.filename ?? "",
  });

  // ⚖ The way BACK to the document that opened this one. Rendered ONLY when
  // there is one: a file picked from the tree, the Acervo or an attachment card
  // has no origin, and a back button that sometimes does nothing is a lie about
  // where the lawyer has been. The chain lives on the ref itself, so each hop
  // restores the ref that was showing — presentation and reading position, not
  // merely a path. See the store's `openedFrom` and
  // `ai-docs/lexide-viewer-return-to-origin-miniplan.md`.
  const origin = file.openedFrom ?? null;
  const backLabel = t("workspace.filePane.viewer.back", {
    name: origin ? labelFor(origin, map) : "",
  });

  return (
    <>
      <header
        data-testid="workspace-file-pane-viewer-infobar"
        className="flex items-center gap-3 overflow-x-auto overflow-y-hidden border-b border-slate-700/40 bg-slate-900 px-3 py-1 text-[13px] tracking-tight text-slate-200 dark:border-coffee-700/60 dark:bg-coffee-900 dark:text-cream-300"
      >
        {origin && (
          <button
            type="button"
            data-testid="workspace-file-pane-viewer-back"
            data-back-path={origin.path}
            onClick={() =>
              // ⚠ Deliberately NOT `event.currentTarget`. This button unmounts
              // the moment the pane re-renders into the origin file, so storing
              // it as the trigger means `closeFile`'s `document.contains` guard
              // is false and Esc returns focus NOWHERE — the layout-plan §7.5
              // contract broken silently, by the guard that exists to make it
              // safe. Carry the ORIGINAL opener (the tree row) through instead.
              // Read at click time via `getState` rather than subscribed: the
              // store's own docblock keeps `trigger` out of the render graph.
              openFile(origin, useWorkspaceFileStore.getState().trigger)
            }
            aria-label={backLabel}
            title={backLabel}
            // LEADING, before the title chip: this is the only control in the
            // bar that is about somewhere ELSE, and the rest of the bar is
            // about the file currently open.
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-slate-700/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:text-cream-300 dark:hover:bg-coffee-800/70 dark:hover:text-cream-50"
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <InfoChip
          data-testid="workspace-file-pane-title"
          icon={FileText}
          label={currentName}
          title={detailsLabel}
          onClick={handleOpen}
        />
        {children}
        {/* Click-anywhere spacer — opens the same details dialog. */}
        <button
          type="button"
          data-testid="workspace-file-pane-viewer-infobar-spacer"
          onClick={handleOpen}
          aria-label={detailsLabel}
          title={detailsLabel}
          className="h-full flex-1 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        />
        {download && (
          <button
            type="button"
            data-testid="workspace-file-pane-download"
            data-pending={download.pending || undefined}
            onClick={download.onDownload}
            disabled={download.pending}
            aria-label={downloadLabel}
            title={downloadLabel}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-slate-700/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-40 dark:text-cream-300 dark:hover:bg-coffee-800/70 dark:hover:text-cream-50"
          >
            {download.pending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
          </button>
        )}
        {onClose && (
          <button
            type="button"
            data-testid="workspace-file-pane-close"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeLabel}
            // Prominent (operator 2026-07-30): the viewer's close is a primary
            // control — sized up with a visible resting border, not chrome-shy.
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-500/60 text-slate-200 transition-colors hover:border-red-400/70 hover:bg-red-500/15 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:border-coffee-500/70 dark:text-cream-200 dark:hover:border-red-400/60 dark:hover:bg-red-500/20 dark:hover:text-red-200"
          >
            <X size={16} strokeWidth={2.25} />
          </button>
        )}
      </header>
      {detailsOpen && (
        <FileDisplayDetailsDialog
          node={{
            path: file.path,
            isDirectory: false,
            name: entry?.name ?? basename(file.path),
            alias: entry?.alias,
            tag: entry?.tag,
            title: entry?.title,
            description: entry?.description,
          }}
          siblingEffectiveNames={map.siblingEffectiveNames(file.path)}
          onClose={() => setDetailsOpen(false)}
        />
      )}
    </>
  );
}

interface InfoChipProps {
  icon: typeof FileText;
  label: string;
  title?: string;
  onClick?: () => void;
  /** Optional testid forwarded to the rendered element. The alias
   *  chip uses `workspace-file-pane-title` for E2E continuity with
   *  the (now-removed) shell topbar's title slot. */
  "data-testid"?: string;
}

export function InfoChip({
  icon: Icon,
  label,
  title,
  onClick,
  "data-testid": testid,
}: InfoChipProps) {
  const content = (
    <>
      <Icon size={14} className="shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 max-w-[20rem] truncate">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        title={title}
        className="-mx-1 flex shrink-0 items-center gap-1 rounded px-1 hover:bg-slate-700/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-coffee-800/60"
      >
        {content}
      </button>
    );
  }
  return (
    <span data-testid={testid} className="flex shrink-0 items-center gap-1" title={title}>
      {content}
    </span>
  );
}
